import React, { useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAppSelector, useAppDispatch } from '@/store';
import { fetchWifiInfo } from '@/store/slices/residents/wifiSlice';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { homeStrings } from '@/constants/residents/strings';
import { WifiConnectionStatus } from '@/types/residents/wifi';

interface WiFiCardProps {
  onPress: () => void;
}

export const WiFiCard: React.FC<WiFiCardProps> = ({ onPress }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { wifiInfo, connectionStatus } = useAppSelector((state) => state.wifi);

  useEffect(() => {
    // Fetch WiFi info when component mounts if not already loaded
    if (!wifiInfo && user?.currentLeaseId) {
      dispatch(fetchWifiInfo(user.currentLeaseId));
    }
  }, [dispatch, wifiInfo, user?.currentLeaseId]);

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case WifiConnectionStatus.CONNECTED:
        return homeStrings.wifi.button.connected;
      case WifiConnectionStatus.CONNECTING:
        return homeStrings.wifi.button.connecting;
      case WifiConnectionStatus.DISCONNECTED:
        return 'Not Connected';
      case WifiConnectionStatus.UNKNOWN:
      default:
        return homeStrings.wifi.description;
    }
  };

  const showConnectionStatus = wifiInfo && connectionStatus !== WifiConnectionStatus.UNKNOWN;

  return (
    <TouchableOpacity 
      style={styles.card} 
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${homeStrings.wifi.title}${showConnectionStatus ? `. ${getConnectionStatusText()}` : ''}`}
    >
      <View style={styles.content}>
        <View style={styles.cbxLogo}>
          <Text style={styles.cbxLogoText}>CBX</Text>
        </View>
        <Text style={styles.title}>{homeStrings.wifi.title}</Text>
        <Text style={styles.description}>
          {homeStrings.wifi.description}
        </Text>
      </View>
      {/* Chevron positioned at top-right */}
      <Icon 
        name="chevron-forward" 
        size={24} 
        color={MARKETPLACE_COLORS.inputBorder}
        style={styles.chevron}
      />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    position: 'relative',
  },
  content: {
    // Content area
  },
  chevron: {
    position: 'absolute',
    top: 20,
    right: 20,
  },
  cbxLogo: {
    width: 70,
    height: 30,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  cbxLogoText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: MARKETPLACE_COLORS.textPrimary,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: 2,
  },
  description: {
    fontSize: 13,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textMuted,
  },
});
